import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useLazyGetDatasetQuestionsQuery,
  useLazyGetDatasetQuestionsListQuery,
} from "@/store/api/dataset-api";
import type { DatasetQuestionsSearchResponse, QuestionDetail } from "@/models/Dataset";
import type {
  ScorecardBrand,
  ScorecardQuestionRow,
  ScorecardRowBase,
  ScorecardSourceQuestionKind,
} from "@/models/Scorecard";
import {
  getBrandCandidates,
  getSourceQuestionKind,
  isEligibleSourceBrandQuestionSummary,
  normalizeQuestionType,
  toMatchableCategories,
} from "@/utils/scorecard-utils";
import { wouldCreateCycle } from "@/utils/scorecard-row-lifecycle-utils";
import type { GridMode } from "@components/charts/scorecard-config/components/QuestionRowEditor/GridValueOrRollupSelector";
import { useRowBrandMapping } from "./useRowBrandMapping";

export type QuestionRowBaseMode = "none" | "existing_question_row" | "new_question_base";

/**
 * Owns Scorecard Row Base selection for a Question row builder session:
 * either a reference to another eligible Percentage Question row, or an
 * independent New Question Base definition. Both sources go through the same
 * crosstab-diagonal fetch mechanism, so like Existing Question Row a value/
 * rollup pick is only needed for a Single grid question (to narrow its
 * value axis, keeping its variable axis as the per-brand cross-columns) —
 * a plain Single/Multiple question's full set of categories already forms
 * the cross axis, matched diagonally via this metric's own brand mapping.
 * Doesn't reuse useGridMetricSelection as-is since that hook's value/rollup
 * state is keyed to a row's own editingRow shape, not a nested metric.
 */
export function useQuestionRowBaseSelection(
  datasetId: string | null,
  editingRowId: string | undefined,
  otherRows: ScorecardQuestionRow[],
  brands: ScorecardBrand[],
  initialBase: ScorecardRowBase | null | undefined,
) {
  const [mode, setMode] = useState<QuestionRowBaseMode>(initialBase?.kind ?? "none");
  const [existingRowId, setExistingRowId] = useState(
    initialBase?.kind === "existing_question_row" ? initialBase.questionRowId : "",
  );
  const initialQuestionBase =
    initialBase?.kind === "new_question_base" ? initialBase.questionBase : null;

  const eligibleBaseRows = useMemo(
    () =>
      otherRows.filter(
        (r) =>
          r.resultType === "percentage" &&
          r.id !== editingRowId &&
          !wouldCreateCycle(otherRows, editingRowId ?? "__new__", r.id),
      ),
    [otherRows, editingRowId],
  );

  // --- New Question Base's own question search (mirrors QuestionRowBuilder's) ---
  const [searchTerm, setSearchTerm] = useState("");
  const [accordionExpanded, setAccordionExpanded] = useState(!initialQuestionBase);
  const [selectedQuestionId, setSelectedQuestionId] = useState(
    initialQuestionBase?.sourceQuestionId ?? "",
  );
  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);

  const observerTarget = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [triggerQuestions, { data: questionsData, isFetching }] =
    useLazyGetDatasetQuestionsQuery();
  const [fetchQuestionDetails, { isFetching: isFetchingDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  useEffect(() => {
    if (!initialQuestionBase || !datasetId) return;
    fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: initialQuestionBase.sourceQuestionLabel,
      dataset_element_id: initialQuestionBase.sourceQuestionId,
      expanded: true,
    })
      .unwrap()
      .then((result) => {
        const detail = result.questions.find(
          (q) => q.datasetElementId === initialQuestionBase.sourceQuestionId,
        );
        setQuestionDetail(detail ?? null);
        if (detail) restoreSelection(detail);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!datasetId || !accordionExpanded || mode !== "new_question_base") return;
    triggerQuestions({
      id: datasetId,
      page: 1,
      page_size: 50,
      question_type: ["SINGLE", "MULTI"],
      multidimensional: true,
      search_query: debouncedSearch || undefined,
    });
  }, [datasetId, debouncedSearch, accordionExpanded, mode, triggerQuestions]);

  const eligibleQuestions = useMemo(
    () => (questionsData?.questions ?? []).filter(isEligibleSourceBrandQuestionSummary),
    [questionsData],
  );

  const sourceQuestionKind: ScorecardSourceQuestionKind | null = useMemo(
    () => (questionDetail ? getSourceQuestionKind(questionDetail) : null),
    [questionDetail],
  );
  const isVariableFocus =
    questionDetail != null && normalizeQuestionType(questionDetail.type) === "MULTIPLE";

  // --- Value/rollup selection — always required, regardless of kind ---
  const [gridMode, setGridModeState] = useState<GridMode>(
    initialQuestionBase?.rollup ? "rollup" : "value",
  );
  // Resolved to the matching value/variable id once questionDetail loads
  // (see restoreSelection) — only the label/code are persisted, since that's
  // all getCrossFocusParamsForBaseRow needs downstream.
  const [selectedValueId, setSelectedValueId] = useState("");
  const [selectedValueLabel, setSelectedValueLabel] = useState(
    initialQuestionBase?.metricValueLabel ?? "",
  );
  const [selectedValueCode, setSelectedValueCode] = useState(
    initialQuestionBase?.metricValueCode ?? "",
  );
  const [rollupName, setRollupName] = useState(initialQuestionBase?.rollup?.name ?? "");
  const [rollupSelectedCodes, setRollupSelectedCodes] = useState<string[]>(
    initialQuestionBase?.rollup?.sourceCategoryLabels ?? [],
  );

  /** Resolves the saved metricValueCode back to a dropdown-selectable id —
   *  only needed for single-value mode; rollupSelectedCodes already restored
   *  directly from the saved rollup's own sourceCategoryLabels. */
  function restoreSelection(detail: QuestionDetail) {
    if (!initialQuestionBase || initialQuestionBase.rollup) return;
    if (normalizeQuestionType(detail.type) === "MULTIPLE") {
      const variable = detail.rowVariables.find(
        (v) => v.label === initialQuestionBase.metricValueCode,
      );
      if (variable) setSelectedValueId(variable.id);
    } else {
      const value = detail.values.find(
        (v) => String(v.value) === initialQuestionBase.metricValueCode,
      );
      if (value) setSelectedValueId(value.id);
    }
  }

  const responseCategories = useMemo(
    () => (questionDetail ? toMatchableCategories(getBrandCandidates(questionDetail)) : []),
    [questionDetail],
  );

  const brandMapping = useRowBrandMapping(brands, responseCategories, initialQuestionBase);
  const { mappings } = brandMapping;

  const handleSetMode = (next: QuestionRowBaseMode) => {
    setMode(next);
    if (next !== "existing_question_row") setExistingRowId("");
  };

  const handleQuestionSelect = async (question: DatasetQuestionsSearchResponse) => {
    setSelectedQuestionId(question.datasetElementId);
    setQuestionDetail(null);
    setGridModeState("value");
    setSelectedValueId("");
    setSelectedValueLabel("");
    setSelectedValueCode("");
    setRollupName("");
    setRollupSelectedCodes([]);
    brandMapping.resetForNewQuestion();
    if (!datasetId) return;

    const result = await fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: question.label,
      dataset_element_id: question.datasetElementId,
      expanded: true,
    }).unwrap();
    const detail = result.questions.find(
      (q) => q.datasetElementId === question.datasetElementId,
    );
    setQuestionDetail(detail ?? null);
    setAccordionExpanded(false);
  };

  const handleValueChange = (valueId: string) => {
    setSelectedValueId(valueId);
    if (isVariableFocus) {
      const variable = questionDetail?.rowVariables.find((v) => v.id === valueId);
      setSelectedValueLabel(variable ? variable.rowtitle || variable.title : "");
      setSelectedValueCode(variable?.label ?? "");
    } else {
      const value = questionDetail?.values.find((v) => v.id === valueId);
      setSelectedValueLabel(value ? value.title || value.label : "");
      setSelectedValueCode(value ? String(value.value) : "");
    }
  };

  const handleGridModeChange = (next: GridMode) => {
    setGridModeState(next);
    if (next === "value") {
      setRollupName("");
      setRollupSelectedCodes([]);
    } else {
      setSelectedValueId("");
      setSelectedValueLabel("");
      setSelectedValueCode("");
    }
  };

  // A value/rollup pick only makes sense for a Single grid question, exactly
  // like getCrossFocusParamsForBaseRow's own gating and the row's own
  // useGridMetricSelection.isGridReady: for a plain Single/Multiple question,
  // its full set of response categories/variables already form the diagonal
  // cross axis (matched per brand via this metric's own brand mapping) —
  // picking one value or rollup would collapse that axis to a single column,
  // which is only meaningful when there's a second (grid) axis to narrow.
  const needsValuePick = sourceQuestionKind === "single_grid";
  const isRollupReady =
    !needsValuePick || gridMode !== "rollup" || rollupSelectedCodes.length >= 2;
  const isValueReady = !needsValuePick || gridMode !== "value" || !!selectedValueId;

  /** True once every requirement for the currently-selected mode is met —
   *  "none" is always valid, "existing_question_row" needs a picked row,
   *  "new_question_base" needs a question, a value/rollup, and no unresolved
   *  brand mapping. */
  const isValid = (): boolean => {
    if (mode === "none") return true;
    if (mode === "existing_question_row") return !!existingRowId;
    return (
      !!questionDetail &&
      isValueReady &&
      isRollupReady &&
      !mappings.some((m) => m.status === "unresolved")
    );
  };

  const buildRowBase = (): ScorecardRowBase | null => {
    if (mode === "none") return null;
    if (mode === "existing_question_row") {
      const row = otherRows.find((r) => r.id === existingRowId);
      if (!row) return null;
      return {
        kind: "existing_question_row",
        questionRowId: row.id,
        questionRowLabel: row.label,
      };
    }
    if (!questionDetail || !sourceQuestionKind) return null;
    const isRollupMode = needsValuePick && gridMode === "rollup";
    const isValuePicked = needsValuePick && !isRollupMode;
    return {
      kind: "new_question_base",
      questionBase: {
        id: initialQuestionBase?.id ?? crypto.randomUUID(),
        sourceQuestionId: questionDetail.datasetElementId,
        sourceQuestionLabel: questionDetail.label,
        sourceQuestionTitle: questionDetail.title,
        sourceQuestionType: normalizeQuestionType(questionDetail.type) as
          | "SINGLE"
          | "MULTIPLE",
        sourceQuestionKind,
        valueFocus: null,
        metricValueLabel: isValuePicked ? selectedValueLabel : null,
        metricValueCode: isValuePicked ? selectedValueCode : null,
        rollup: isRollupMode
          ? {
              id: initialQuestionBase?.rollup?.id ?? crypto.randomUUID(),
              name: rollupName.trim(),
              calculationType: "SUM_SELECTED",
              sourceCategoryLabels: rollupSelectedCodes,
            }
          : null,
        rollupValueIds: null,
        brandMappings: mappings,
        responseCategories,
      },
    };
  };

  return {
    mode,
    setMode: handleSetMode,
    existingRowId,
    setExistingRowId,
    eligibleBaseRows,
    // New Question Base question search
    searchTerm,
    setSearchTerm,
    accordionExpanded,
    setAccordionExpanded,
    selectedQuestionId,
    questionDetail,
    eligibleQuestions,
    isFetchingQuestions: isFetching,
    isFetchingDetails,
    observerTarget,
    handleQuestionSelect,
    sourceQuestionKind,
    isVariableFocus,
    // Value/rollup
    gridMode,
    handleGridModeChange,
    selectedValueId,
    handleValueChange,
    rollupName,
    setRollupName,
    rollupSelectedCodes,
    setRollupSelectedCodes,
    // Brand mapping
    brands,
    mappings,
    responseCategories,
    onManualBind: brandMapping.onManualBind,
    onMarkUnavailable: brandMapping.onMarkUnavailable,
    onRestoreMapping: brandMapping.onRestoreMapping,
    isValid,
    buildRowBase,
  };
}
